// Add a platform model key: an Anthropic API key or a Claude subscription token, named, with its
// value typed into an uncontrolled input that is read once, sealed for the platform org in this
// browser and cleared (the Secrets page's ValueInputs).
import { LockKey } from "@phosphor-icons/react";
import { type FormEvent, useState } from "react";
import { Button, Input } from "rizzui";

import { useT } from "../i18n";
import { clearValues, formReader } from "../secrets/form";
import type { Signer } from "../secrets/requests";
import { type Credential, template, type TemplateId } from "../secrets/templates";
import { ValueInputs } from "../secrets/ValueInputs";
import { cn } from "../ui/cn";
import { SideDrawer } from "../ui/SideDrawer";
import { addPlatformKey } from "./api";

const MODEL_TEMPLATES: TemplateId[] = ["claude_subscription", "anthropic_api_key"];

interface Props {
  open: boolean;
  signer: Signer;
  signerName: string;
  orgId: string;
  onClose: () => void;
  onSaved: (c: Credential) => void;
  onError: (message: string) => void;
}

export function AddPlatformKeyDrawer({ open, onClose, ...rest }: Props) {
  const t = useT().secrets.platform;
  return (
    <SideDrawer open={open} onClose={onClose} title={t.drawerTitle} subtitle={t.drawerSubtitle}>
      {open ? <AddForm onClose={onClose} {...rest} /> : null}
    </SideDrawer>
  );
}

function AddForm({ signer, signerName, orgId, onClose, onSaved, onError }: Omit<Props, "open">) {
  const { common, secrets } = useT();
  const [picked, setPicked] = useState<TemplateId>("claude_subscription");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const t = template(picked);

  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    setBusy(true);
    try {
      const saved = await addPlatformKey(signer, orgId, { name, template: t, scope: "", hosts: "", header: "", workerExposed: false }, formReader(form));
      clearValues(form, t.value);
      onSaved(saved);
      onClose();
    } catch (err) {
      onError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="space-y-5" aria-label={secrets.platform.drawerTitle} onSubmit={(e) => void submit(e)}>
      <div className="grid grid-cols-2 gap-3" role="radiogroup" aria-label={secrets.platform.kind}>
        {MODEL_TEMPLATES.map((id) => (
          <button
            key={id}
            type="button"
            role="radio"
            aria-checked={picked === id}
            onClick={() => setPicked(id)}
            className={cn(
              "rounded-lg border p-3 text-start text-[13px] font-semibold",
              picked === id ? "border-primary bg-primary-lighter/50 text-gray-900" : "border-muted text-gray-700 hover:border-gray-300",
            )}
          >
            {template(id).label}
          </button>
        ))}
      </div>
      <Input label={secrets.table.name} value={name} placeholder={t.namePlaceholder} required maxLength={128} onChange={(e) => setName(e.target.value)} />
      <ValueInputs key={picked} shape={t.value} />
      <div className="flex items-center gap-3 border-t border-muted pt-4">
        <span className="me-auto flex min-w-0 items-center gap-1.5 text-xs text-gray-500">
          <LockKey weight="fill" className="h-3.5 w-3.5 shrink-0 text-secondary" /> {secrets.forms.signedBy(signerName)}
        </span>
        <Button variant="outline" onClick={onClose} disabled={busy}>
          {common.actions.cancel}
        </Button>
        <Button type="submit" isLoading={busy}>
          {secrets.platform.save}
        </Button>
      </div>
    </form>
  );
}
